<template>
  <div>
    <div class="tab-pane fade" :class="{ 'show active': isActive }" id="nav-vault-rewards" role="tabpanel"
      aria-labelledby="nav-vault-rewards-tab3" tabindex="1">
      <div class="container">
        <div v-if="!isActive"></div>

        <div v-else-if="loading" class="vault-state-panel vault-state-panel--loading" role="status" aria-live="polite">
          <div class="vault-state-spinner" aria-hidden="true"></div>
          <p class="vault-state-title">Loading vault rewards</p>
          <p class="vault-state-hint">Please wait…</p>
        </div>

        <div v-else-if="error" class="vault-state-panel vault-state-panel--error" role="alert">
          <div class="vault-state-icon vault-state-icon--error" aria-hidden="true">
            <i class="fas fa-exclamation-circle"></i>
          </div>
          <p class="vault-state-title">Unable to load rewards</p>
          <p class="vault-state-message">{{ error }}</p>
        </div>

        <div v-else-if="isEmpty" class="vault-state-panel vault-state-panel--empty">
          <div class="vault-state-icon vault-state-icon--empty" aria-hidden="true">
            <i class="fas fa-gift"></i>
          </div>
          <p class="vault-state-title">No vault rewards yet</p>
          <p class="vault-state-hint">Rewards you earn will appear here when available.</p>
        </div>

        <div v-else>
          <div class="vault">
            <div class="vault-header">
              <div class="flex flex-row items-center justify-between">
                <div class="flex flex-row items-center gap-4">
                  <!-- Vault Icon -->
                  <div class="vault-icon flex items-center justify-center">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M19.2704 5.44334C19.3423 5.31268 19.448 5.20373 19.5764 5.12785C19.7048 5.05196 19.8512 5.01193 20.0004 5.01193C20.1495 5.01193 20.296 5.05196 20.4244 5.12785C20.5528 5.20373 20.6584 5.31268 20.7304 5.44334L25.6504 14.7833C25.7677 14.9996 25.9315 15.1872 26.1299 15.3327C26.3283 15.4782 26.5565 15.5779 26.798 15.6248C27.0396 15.6716 27.2885 15.6644 27.5269 15.6036C27.7654 15.5429 27.9874 15.4301 28.177 15.2733L35.3054 9.16667C35.4422 9.05538 35.6108 8.99037 35.787 8.98101C35.9631 8.97165 36.1377 9.01842 36.2855 9.11458C36.4334 9.21075 36.5469 9.35136 36.6098 9.51616C36.6727 9.68096 36.6816 9.86146 36.6354 10.0317L31.912 27.1083C31.8156 27.4578 31.6079 27.7663 31.3204 27.987C31.0329 28.2078 30.6812 28.3288 30.3187 28.3317H9.68371C9.32094 28.3291 8.9689 28.2083 8.68103 27.9875C8.39317 27.7667 8.18521 27.4581 8.08872 27.1083L3.36705 10.0333C3.3208 9.86313 3.32975 9.68263 3.39262 9.51782C3.45548 9.35302 3.56902 9.21242 3.71688 9.11625C3.86475 9.02008 4.03931 8.97331 4.21545 8.98267C4.39159 8.99203 4.56021 9.05704 4.69705 9.16834L11.8237 15.275C12.0134 15.4317 12.2354 15.5445 12.4738 15.6053C12.7122 15.6661 12.9612 15.6733 13.2027 15.6264C13.4443 15.5796 13.6724 15.4799 13.8709 15.3344C14.0693 15.1889 14.2331 15.0013 14.3504 14.785L19.2704 5.44334Z"
                        stroke="#0F172B" stroke-width="3.33333" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M8.33398 35H31.6673" stroke="#0F172B" stroke-width="3.33333" stroke-linecap="round"
                        stroke-linejoin="round" />
                    </svg>

                  </div>

                  <!-- Vault Title -->
                  <div class="vault-title flex flex-col gap-4">
                    <h3 class="vault-name m-0">{{ selectedVaultTitle }}</h3>

                    <!-- Vault Timeline -->
                    <div class="flex flex-row items-center gap-4">
                      <div class="badge" v-if="selectedVaultStart">
                        <i class="far fa-calendar"></i>
                        <span class="badge-label">Start Date:</span>
                        <span>{{ selectedVaultStart }}</span>
                      </div>

                      <div class="badge" v-if="selectedVaultEnd">
                        <i class="far fa-clock"></i>
                        <span class="badge-label">End Date:</span>
                        <span>{{ selectedVaultEnd }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="vaults.length >= 1" class="vault-select-wrap">
                  <select class="vault-select" v-model="selectedVaultId">
                    <option v-for="v in vaults" :key="String(v.vault_id)" :value="String(v.vault_id)">
                      {{ vaultOptionLabel(v) }}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <div class="vault-body">
              <!-- Vault Rewards -->
              <div class="vault-rewards">
                <!-- Loop Items -->
                <div v-if="!selectedRewards.length" class="reward-item">
                  <div class="reward-ribben">
                    <span>—</span>
                  </div>
                  <div class="flex flex-col">
                    <div class="reward-name">
                      <span>No rewards in this vault</span>
                    </div>
                  </div>
                </div>

                <div v-else v-for="row in decoratedRewards" :key="row.rowKey" class="reward-item">
                  <!-- Ribben -->
                  <div class="reward-ribben" :class="row.rarityClass">
                    <span>{{ row.rarityLabel }}</span>
                  </div>

                  <!-- Reward Content -->
                  <div class="flex flex-col gap-4 h-100">
                    <!-- Reward Image -->
                    <div class="reward-thumbnail">
                      <img v-if="row.imageSrc" :src="row.imageSrc" :alt="row.imageAlt">
                      <span v-else class="reward-thumb-text">
                        {{ row.typeLabel }}
                      </span>
                    </div>

                    <!-- Reward Name -->
                    <div class="reward-name">
                      <span>{{ row.title }}</span>
                    </div>

                    <!-- Reward Status -->
                    <div class="reward-status" :class="`reward-status--${row.statusKey}`">
                      <span v-if="row.statusKey === 'claimed' || row.reward.claimed_at">
                        Claimed. {{ formatDate(row.reward.claimed_at) }}
                      </span>
                      <span v-else-if="row.reward.expire_at">
                        Exp. {{ formatDate(row.reward.expire_at) }}
                      </span>
                      <span v-else>
                        Status not available
                      </span>
                    </div>

                    <!-- Reward Action -->
                    <div class="reward-action">
                      <div v-if="row.showClaimed" class="reward-claimed">
                        <strong>
                          <i class="fas fa-star"></i>
                          <span>Claimed</span>
                          <i class="fas fa-star"></i>
                        </strong>
                      </div>

                      <button v-else-if="row.typeKey === 'digital'" type="button" class="reward-cta"
                        :class="{ 'reward-cta--disabled': row.statusKey === 'expired' }"
                        @click="openDigitalClaim(row.reward)"
                        :disabled="digitalClaimLoading || row.statusKey === 'expired'">
                        {{ row.statusKey === 'expired' ? 'Expired' : 'Redeem' }}
                      </button>

                      <a v-else class="reward-cta" :class="{ 'reward-cta--disabled': row.storeRedeemDisabled }"
                        :href="!row.storeRedeemDisabled ? redeemUrl(row.reward.coupon_code) : null" target="_blank"
                        rel="noopener noreferrer" :aria-disabled="String(row.storeRedeemDisabled)"
                        @click="row.storeRedeemDisabled && $event.preventDefault()">
                        {{ row.statusKey === 'expired' ? 'Expired' : (row.statusKey === 'claimed' ? 'Claimed' :
                        'Redeem') }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Digital claim modal -->
    <div v-if="digitalClaimOpen" class="vault-modal-backdrop">
      <div class="vault-modal">
        <h4 class="vault-modal-title">Claim digital reward</h4>

        <p v-if="digitalClaimError" class="vault-modal-error">{{ digitalClaimError }}</p>
        <p v-else class="vault-modal-text">
          This will submit your request and notify our team for processing.
        </p>

        <div class="vault-modal-actions">
          <button class="reward-cta" type="button" @click="closeDigitalClaim" :disabled="digitalClaimLoading">
            Cancel
          </button>
          <button class="reward-cta" type="button" @click="confirmDigitalClaim" :disabled="digitalClaimLoading">
            {{ digitalClaimLoading ? "Please wait..." : "Confirm" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Service from "@/services";
import dayjs from "dayjs";

const RARITY_BY_INDEX = ["none", "common", "rare", "epic", "legendary"];

function extractError(e) {
  if (!e) return "Unknown error";
  if (typeof e === "string") return e;
  if (e?.response?.data?.message) return e.response.data.message;
  if (e?.response?.data?.error) return e.response.data.error;
  if (e?.message) return e.message;
  try {
    return JSON.stringify(e);
  } catch {
    return "Unknown error";
  }
}

export default {
  name: "VaultRewardsTab",
  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    userGems: {
      type: [Number, String],
      default: null,
    },
    userCoins: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      vaultRewards: null,
      vaults: [],
      selectedVaultId: null,
      hasLoadedOnce: false,

      digitalClaimOpen: false,
      digitalClaimLoading: false,
      digitalClaimError: null,
      digitalClaimReward: null,
    };
  },
  computed: {
    isEmpty() {
      if (!this.vaultRewards) return true;
      return !this.vaults.length;
    },

    selectedVault() {
      const id = this.selectedVaultId;
      if (!id) return this.vaults?.[0] || null;
      return this.vaults.find(v => String(v.vault_id) === String(id)) || this.vaults?.[0] || null;
    },

    selectedRewards() {
      const rewards = this.selectedVault?.rewards;
      return Array.isArray(rewards) ? rewards : [];
    },

    selectedVaultTitle() {
      const v = this.selectedVault;
      return v?.vault_name || `Vault ${v?.vault_id ?? ""}`.trim();
    },

    selectedVaultStart() {
      const v = this.selectedVault;
      if (!v) return null;
      const parts = [v.vault_start_date, v.vault_start_time].filter(Boolean);
      return parts.length ? this.formatDate(parts.join(" ")) : null;
    },

    selectedVaultEnd() {
      const v = this.selectedVault;
      if (!v) return null;
      const parts = [v.vault_end_date, v.vault_end_time].filter(Boolean);
      return parts.length ? this.formatDate(parts.join(" ")) : null;
    },

    /** One row object per reward: avoids repeated status/type work in the template. */
    decoratedRewards() {
      return this.selectedRewards.map((reward, index) => {
        const typeKey = String(reward.reward_type ?? "").trim().toLowerCase();
        const statusKey = this.displayStatusKey(reward);
        const r = reward.rarity;
        const rarityKey =
          typeof r === "number"
            ? RARITY_BY_INDEX[r] || "none"
            : String(r ?? "none").toLowerCase();
        const rid =
          reward.reward_id != null && reward.reward_id !== ""
            ? String(reward.reward_id)
            : "";
        const title =
          typeKey === "physical"
            ? reward.product_name || reward.reward_name || rid || "Vault Reward"
            : reward.reward_name || rid || "Vault Reward";
        let imageAlt = typeKey ? typeKey.toUpperCase() : "REWARD";
        if (typeKey === "physical") imageAlt = title || "Physical reward";
        else if (typeKey === "digital") imageAlt = title || "Digital reward";

        return {
          reward,
          rowKey: this.rewardRowKey(reward, index),
          typeKey,
          statusKey,
          typeLabel: typeKey ? typeKey.toUpperCase() : "REWARD",
          title,
          imageSrc:
            typeKey === "physical"
              ? reward.product_image || reward.reward_image || ""
              : reward.reward_image || "",
          imageAlt,
          rarityLabel: rarityKey ? rarityKey.toUpperCase() : "NONE",
          rarityClass: rarityKey ? `reward-ribben--${rarityKey}` : "",
          showClaimed:
            this.statusKey(reward.status) === "claimed" ||
            typeKey === "currency" ||
            typeKey === "garage",
          storeRedeemDisabled:
            statusKey === "expired" ||
            statusKey === "claimed" ||
            (typeKey === "physical" && !reward.coupon_code),
        };
      });
    },
  },
  watch: {
    isActive: {
      immediate: true,
      handler(active) {
        if (active && !this.hasLoadedOnce) {
          this.fetchRewards();
        }
      },
    },
  },
  methods: {
    /** Stable :key from API fields only (reward_id, coupon_code, voucher_code). */
    rewardRowKey(reward, index) {
      const parts = [];
      if (reward.reward_id != null && reward.reward_id !== "") {
        parts.push(String(reward.reward_id));
      }
      if (reward.coupon_code) parts.push(String(reward.coupon_code));
      if (reward.voucher_code) parts.push(String(reward.voucher_code));
      return parts.length ? parts.join(":") : `reward-${index}`;
    },

    vaultOptionLabel(v) {
      const name = v?.vault_name || `Vault ${v?.vault_id ?? ""}`.trim();
      const n = Array.isArray(v?.rewards) ? v.rewards.length : 0;
      return `${name} (${n})`;
    },

    formatDate(iso) {
      if (!iso) return "";
      return dayjs(iso).isValid() ? dayjs(iso).format("DD/MM/YYYY - hh:mm A") : String(iso);
    },

    statusKey(status) {
      return (status || "pending").toLowerCase();
    },

    /** True when current time is after expire_at (API status may still be pending). */
    isRewardPastExpiry(reward) {
      const at = reward?.expire_at;
      if (!at) return false;
      const end = dayjs(at);
      return end.isValid() && dayjs().isAfter(end);
    },

    /** UI status: claimed unchanged; otherwise expired if past expiry or API says expired. */
    displayStatusKey(reward) {
      if (this.statusKey(reward?.status) === "claimed") return "claimed";
      if (this.isRewardPastExpiry(reward) || this.statusKey(reward?.status) === "expired") {
        return "expired";
      }
      return this.statusKey(reward?.status);
    },

    redeemUrl(couponCode) {
      const base = (process.env.VUE_APP_WHOOSH_STORE_BASE_URL || "").replace(/\/$/, "");
      return `${base}/redeem?coupon=${encodeURIComponent(couponCode)}`;
    },

    openDigitalClaim(reward) {
      this.digitalClaimReward = reward || null;
      this.digitalClaimError = null;
      this.digitalClaimOpen = true;
    },

    closeDigitalClaim() {
      if (this.digitalClaimLoading) return;
      this.digitalClaimOpen = false;
      this.digitalClaimError = null;
      this.digitalClaimReward = null;
    },

    async confirmDigitalClaim() {
      const reward = this.digitalClaimReward;
      if (!reward?.reward_id) {
        this.digitalClaimError = "Invalid reward. Please refresh and try again.";
        return;
      }

      this.digitalClaimLoading = true;
      this.digitalClaimError = null;

      try {
        const raw = await Service().post(`../api/vault-rewards/digital/claim`, {
          reward_id: String(reward.reward_id),
        });
        const res = raw && typeof raw.status === "number" && raw.data ? raw.data : raw;

        if (!res || res.status !== true) {
          this.digitalClaimError = res?.message || "Unable to claim this reward.";
          return;
        }

        // Optimistic UI update.
        reward.status = "claimed";
        reward.claimed_at = new Date().toISOString();

        this.digitalClaimOpen = false;
        this.digitalClaimReward = null;
        this.digitalClaimError = null;
      } catch (e) {
        this.digitalClaimError = extractError(e);
      } finally {
        this.digitalClaimLoading = false;
      }
    },

    async fetchRewards() {
      this.loading = true;
      this.error = null;
      this.vaultRewards = null;
      this.vaults = [];
      this.selectedVaultId = null;

      const playerId = localStorage.getItem("uuid");
      if (!playerId) {
        this.error = "Player id is not available. Please sign in again.";
        this.loading = false;
        this.hasLoadedOnce = true;
        return;
      }

      try {
        const raw = await Service().get(
          `../api/vault-rewards?player_id=${encodeURIComponent(playerId)}`
        );
        const res = raw && typeof raw.status === "number" && raw.data ? raw.data : raw;

        if (!res || res.status !== true) {
          this.error = res?.message || "Unable to load vault rewards.";
          this.vaultRewards = res || null;
          return;
        }

        this.vaultRewards = res;
        const data = res?.data;
        this.vaults = Array.isArray(data) ? data : [];
        this.selectedVaultId = this.vaults.length ? String(this.vaults[0].vault_id) : null;
      } catch (e) {
        this.error = extractError(e);
      } finally {
        this.loading = false;
        this.hasLoadedOnce = true;
      }
    },
  },
};
</script>

<style scoped>
/* Utilities classes */
.flex {
  display: flex;
}

.flex-row {
  flex-direction: row;
}

.flex-col {
  display: flex;
  flex-direction: column;
}

.items-start {
  align-items: flex-start;
}

.items-end {
  align-items: flex-end;
}

.items-center {
  align-items: center;
}

.items-stretch {
  align-items: stretch;
}

.justify-start {
  justify-content: flex-start;
}

.justify-end {
  justify-content: flex-end;
}

.justify-center {
  justify-content: center;
}

.justify-around {
  justify-content: space-around;
}

.justify-stretch {
  justify-content: stretch;
}

.justify-between {
  justify-content: space-between;
}

.flex-wrap {
  flex-wrap: wrap;
}

.gap-1 {
  gap: 0.25rem;
}

.gap-2 {
  gap: 0.5rem;
}

.gap-3 {
  gap: 0.75rem;
}

.gap-4 {
  gap: 1rem;
}

.m-0 {
  margin: 0 !important;
}

.h-100 {
  height: 100%;
}

/* Loading / error / empty section style */
.vault-state-panel {
  align-items: center;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  box-shadow: 0px 6px 12px 2px #23272f1f;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 2rem 0;
  min-height: 400px;
  padding: 2.5rem 1.5rem;
  text-align: center;
}

.vault-state-panel--loading {
  background: radial-gradient(100.72% 837.5% at 0% 0%, rgba(234, 179, 8, 0.08) 0%, rgba(255, 255, 255, 0) 55%),
    #ffffff;
}

.vault-state-spinner {
  animation: vault-state-spin 0.85s linear infinite;
  border: 3px solid #e2e8f0;
  border-radius: 50%;
  border-top-color: #fcba12;
  height: 48px;
  margin-bottom: 1.25rem;
  width: 48px;
}

@keyframes vault-state-spin {
  to {
    transform: rotate(360deg);
  }
}

.vault-state-icon {
  align-items: center;
  border-radius: 50%;
  display: flex;
  font-size: 2.5rem;
  height: 5rem;
  justify-content: center;
  margin-bottom: 1.25rem;
  width: 5rem;
}

.vault-state-icon i {
  font-size: inherit;
}

.vault-state-icon--error {
  background: rgba(220, 38, 38, 0.1);
  color: #dc2626;
}

.vault-state-icon--empty {
  background: linear-gradient(135deg, rgba(255, 223, 32, 0.25) 0%, rgba(225, 113, 0, 0.12) 100%);
  color: #b45309;
}

.vault-state-title {
  color: #1a1d21;
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin: 0 0 0.5rem 0;
  max-width: 28rem;
}

.vault-state-hint {
  color: #64748b;
  font-size: 0.9375rem;
  font-weight: 500;
  line-height: 1.5;
  margin: 0;
  max-width: 22rem;
}

.vault-state-message {
  color: #475569;
  font-size: 0.9375rem;
  line-height: 1.5;
  margin: 0;
  max-width: 32rem;
  word-break: break-word;
}

.vault-state-panel--error .vault-state-title {
  color: #991b1b;
}

/* Vault css styles */
.vault {
  padding: 2.5rem 0;
}

.vault-header {
  background: radial-gradient(100.72% 837.5% at 0% 0%, rgba(234, 179, 8, 0.1) 0%, rgba(0, 0, 0, 0) 50%);
  border-radius: 1rem;
  box-shadow: 0px 6px 10px 2px #0000001A;
  margin-bottom: 2rem;
  padding: 2rem;
}

.vault-icon {
  background: linear-gradient(135deg, #FFDF20 0%, #E17100 100%);
  border-top: 1px solid #FFF08533;
  border-radius: 1rem;
  box-shadow: 0px 0px 30px 0px #FBBF2433;
  height: 80px;
  width: 80px;
}

.vault-title {
  position: relative;
}

.vault-name {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 800;
  color: #1a1d21;
  letter-spacing: -0.01em;
}

.vault-header .badge {
  align-items: center;
  border-radius: 10px;
  border: 1px solid #1D293D1A;
  color: #3C3D3F;
  display: flex;
  font-size: 0.875rem;
  font-weight: 500;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem;
}

.vault-header .badge i {
  color: #F0B100;
  font-size: 1rem;
  margin-right: 0.25rem;
}

.vault-header .badge-label {
  color: #90A1B9;
}

.vault-select-wrap {
  align-items: center;
  display: inline-flex;
  max-width: 100%;
  min-width: 260px;
  position: relative;
}

.vault-select {
  -moz-appearance: none;
  -webkit-appearance: none;
  appearance: none;
  background-color: #F6F6F6;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-position: right 1rem center;
  background-repeat: no-repeat;
  background-size: 1.5rem;
  border-radius: 0.875rem;
  border: 1px solid #E2E8F0 !important;
  color: #314158;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 400;
  max-width: 100%;
  min-width: 220px;
  outline: none;
  padding: 0.75rem 2rem 0.75rem 1rem;
  width: 100%;
}

.vault-select:focus {
  border-color: rgba(252, 184, 19, 0.5) !important;
  box-shadow: 0 0 0 2px rgba(252, 184, 19, 0.18);
}

.vault-body {
  position: relative;
}

.vault-rewards {
  display: grid;
  gap: 2rem;
  grid-template-columns: repeat(3, 1fr);
  justify-items: start;
}

.reward-item {
  background: #FFFFFF;
  border-radius: 1rem;
  box-shadow: 0px 6px 12px 2px #23272F1F;
  padding: 1rem 1rem 1rem 116px;
  position: relative;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  width: 100%;
}

.reward-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
}

.reward-ribben {
  border-bottom-left-radius: 1rem;
  border-top-left-radius: 1rem;
  bottom: 0;
  color: #ffffff;
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  left: 0;
  position: absolute;
  text-transform: uppercase;
  top: 0;
  width: 100px;
}

.reward-ribben::after {
  content: "";
  height: 100%;
  position: absolute;
  right: 1px;
  top: 0;
  width: 4px;
  background: repeating-linear-gradient(to bottom,
      #F4F4F4 0px,
      #F4F4F4 14px,
      transparent 14px,
      transparent 28px);
}

.reward-ribben>span {
  display: flex;
  align-items: center;
  justify-content: center;
  transform: rotate(-90deg);
  width: 100%;
  height: 100%;
}

.reward-ribben--common {
  background: #45C756;
}

.reward-ribben--rare {
  background: #1CA8EF;
}

.reward-ribben--epic {
  background: #A552FF;
}

.reward-ribben--legendary {
  background: #FFB700;
}

.reward-thumbnail {
  display: flex;
  align-items: center;
  justify-content: center;
}

.reward-thumbnail img {
  display: block;
  height: auto;
  max-height: 8rem;
  max-width: 14rem;
  width: auto;
}

.reward-thumb-text {
  color: #B2B2B2;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 1rem;
  text-align: center;
  text-transform: uppercase;
}

.reward-name {
  color: #232027;
  font-size: 1rem;
  font-weight: 700;
  text-align: center;
  text-transform: capitalize;
}

.reward-status {
  color: #B2B2B2;
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
  margin: auto 0 0.5rem 0;
}

.reward-status--pending {
  color: #FCBA12;
}

.reward-status--claimed {
  color: #B2B2B2;
}

.reward-status--expired {
  color: #dc2626;
}

.reward-action {
  display: flex;
  align-items: center;
  justify-content: center;
}

.reward-cta,
button.reward-cta {
  align-items: center;
  background: #ffffff;
  border-radius: 2rem;
  border: 1px solid #ECE7F8;
  color: #232027;
  cursor: pointer;
  display: inline-flex;
  font-size: 1rem;
  font-weight: 600;
  justify-content: center;
  padding: 0.5rem 1rem;
  text-decoration: none;
  width: 100%;
}

.reward-cta:hover,
button.reward-cta:hover {
  background: #FFDF20;
  border: 1px solid #FFDF20;
  color: #232027;
}

.reward-cta.reward-cta--disabled,
.reward-cta.reward-cta--disabled:hover {
  color: #DA2E2E;
  border: 1px solid #DA2E2E;
  cursor: not-allowed;
  pointer-events: none;
}

.reward-claimed {
  align-items: center;
  background: #ffffff;
  border-radius: 1rem;
  border: 2px solid #4D9F1A;
  color: #4D9F1A;
  display: inline-flex;
  font-size: 1rem;
  font-weight: 800;
  gap: 0.5rem;
  justify-content: center;
  margin-top: -0.5rem;
  padding: 2px;
  text-transform: uppercase;
  transform: rotate(-3deg);
  width: auto;
}

.reward-claimed strong {
  align-items: center;
  border-radius: 0.75rem;
  border: 1px solid #4D9F1A;
  display: inline-flex;
  gap: 0.5rem;
  justify-content: center;
  padding: 0.5rem 0.75rem;
}

.reward-claimed i {
  font-size: 1rem;
}

/* Digital Reward claim modal */
.vault-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.vault-modal {
  width: min(520px, 92vw);
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.vault-modal-title {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 700;
}

.vault-modal-text {
  margin: 0 0 12px 0;
  color: #475569;
}

.vault-modal-error {
  margin: 0 0 12px 0;
  color: #b91c1c;
}

.vault-modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}
</style>